<template>
  <div class="invoice-management">
    <!-- Filter buttons -->
    <div class="invoice-filter mb-4">
      <div class="button-group">
        <button
          :class="['filter-btn', 'all-btn', { active: currentFilter === 'all' }]"
          @click="filterInvoices('all')"
        >
          Tous les factures
        </button>
        <button
          :class="['filter-btn', 'paid-btn', { active: currentFilter === 'paid' }]"
          @click="filterInvoices('paid')"
        >
          Factures Payées
        </button>
        <button
          :class="['filter-btn', 'unpaid-btn', { active: currentFilter === 'unpaid' }]"
          @click="filterInvoices('unpaid')"
        >
          Factures Non Payées
        </button>
      </div>
    </div>

    <!-- Invoice Table -->
    <b-table
      :items="filteredInvoices"
      :fields="tableFields"
      :current-page="currentPage"
      :per-page="pageLength"
      :total-rows="count"
      striped
      hover
      responsive
      show-empty
      @filtered="onFiltered"
    >
      <template #cell(created_at)="data">
        {{ formatDateTime(data.item.created_at) }}
      </template>

      <template #cell(start_date)="data">
        {{ formatDate(data.item.start_date) }}
      </template>

      <template #cell(end_date)="data">
        {{ formatDate(data.item.end_date) }}
      </template>

      <template #cell(total_amount)="data">
        {{ formatCurrency(data.item.total_amount) }}
      </template>

      <template #cell(pricing_breakdown)="data">
        <div
          v-if="data.item.pricing_breakdown"
          class="pricing-summary"
        >
          <!-- Tiered mode: show only plan + price per order -->
          <template v-if="data.item.pricing_mode === 'tiered'">
            <small class="d-block">
              <strong>Plan:</strong>
              <b-badge
                :variant="data.item.pricing_tier_name ? 'primary' : 'warning'"
                class="ml-1"
              >
                {{ data.item.pricing_tier_name || 'Entreprise' }}
              </b-badge>
            </small>
            <small class="d-block mt-1">
              <strong>Prix/colis:</strong>
              {{ data.item.pricing_unit_price ? formatCurrency(data.item.pricing_unit_price) : 'négocié' }}
            </small>
          </template>
          <!-- Flat mode -->
          <template v-else>
            <small class="d-block">
              <strong>Sous-traitance:</strong> {{ data.item.pricing_breakdown.outsourcing_orders }}
              ({{ formatCurrency(data.item.pricing_breakdown.outsourcing_total) }})
            </small>
            <small class="d-block">
              <strong>Direct:</strong> {{ data.item.pricing_breakdown.company_orders }}
              ({{ formatCurrency(data.item.pricing_breakdown.company_total) }})
            </small>
          </template>
        </div>
        <span v-else>-</span>
      </template>

      <template #cell(status)="data">
        <b-badge
          :variant="getStatusVariant(data.item.status)"
          :class="isAdminDeveloper && data.item.status !== 'paid' ? 'cursor-pointer' : ''"
          @click="isAdminDeveloper && data.item.status !== 'paid' ? toggleStatus(data.item) : null"
        >
          {{ data.item.status_display }}
          <feather-icon
            v-if="isAdminDeveloper && data.item.status !== 'paid'"
            icon="CheckCircleIcon"
            size="12"
            class="ml-1"
          />
          <feather-icon
            v-if="data.item.status === 'paid'"
            icon="LockIcon"
            size="12"
            class="ml-1"
          />
        </b-badge>
      </template>

      <template #cell(actions)="data">
        <div class="d-flex gap-2">
          <b-button
            size="sm"
            variant="info"
            @click="showOrderDetails(data.item)"
          >
            Voir détails
          </b-button>
          <b-button
            size="sm"
            variant="primary"
            @click="showInvoice(data.item)"
          >
            Voir facture
          </b-button>
          <b-button
            v-if="isAdminDeveloper && data.item.status !== 'paid'"
            v-b-tooltip.hover
            title="Modifier"
            size="sm"
            variant="warning"
            @click="editInvoice(data.item)"
          >
            <feather-icon
              icon="EditIcon"
              size="14"
            />
          </b-button>
          <b-button
            v-if="isAdminDeveloper && data.item.status !== 'paid'"
            v-b-tooltip.hover
            title="Supprimer"
            size="sm"
            variant="danger"
            @click="deleteInvoice(data.item)"
          >
            <feather-icon
              icon="TrashIcon"
              size="14"
            />
          </b-button>
          <b-badge
            v-if="isAdminDeveloper && data.item.status === 'paid'"
            variant="light-secondary"
            class="px-2 py-1"
          >
            <feather-icon
              icon="LockIcon"
              size="12"
              class="mr-1"
            />
            Payée
          </b-badge>
        </div>
      </template>
    </b-table>

    <!-- Pagination -->
    <b-pagination
      v-model="currentPage"
      :total-rows="count"
      :per-page="pageLength"
      align="center"
      class="my-4"
    />

    <!-- Order Details Modal -->
    <b-modal
      v-model="showModal"
      size="xl"
      hide-footer
      hide-header
      body-class="p-0"
      dialog-class="invoice-modal-dialog"
    >
      <div class="idm-wrapper">

        <!-- ───── Gradient Header ───── -->
        <div class="idm-header">
          <div class="idm-header-left">
            <span class="idm-invoice-number">Facture #{{ selectedOrder.id }}</span>
            <div class="idm-badges mt-2">
              <span
                class="idm-status-badge"
                :class="selectedOrder.status === 'paid' ? 'idm-status-paid' : 'idm-status-unpaid'"
              >
                <feather-icon
                  :icon="selectedOrder.status === 'paid' ? 'CheckCircleIcon' : 'ClockIcon'"
                  size="12"
                  class="mr-1"
                />
                {{ selectedOrder.status_display }}
              </span>
              <span
                v-if="selectedOrder.pricing_tier_name"
                class="idm-tier-badge ml-2"
              >
                <feather-icon icon="LayersIcon" size="12" class="mr-1" />
                {{ selectedOrder.pricing_tier_name }}
              </span>
              <span
                v-else-if="selectedOrder.pricing_mode === 'tiered'"
                class="idm-tier-badge idm-tier-enterprise ml-2"
              >
                Entreprise
              </span>
            </div>
          </div>
          <div class="idm-header-right">
            <div class="idm-period">
              <feather-icon icon="CalendarIcon" size="14" class="mr-1" />
              {{ formatDate(selectedOrder.start_date) }} — {{ formatDate(selectedOrder.end_date) }}
            </div>
          </div>
          <button class="idm-close-btn" @click="showModal = false">
            <feather-icon icon="XIcon" size="18" />
          </button>
        </div>

        <!-- ───── Metric Cards ───── -->
        <div class="idm-metrics">
          <div class="idm-metric idm-metric--blue">
            <div class="idm-metric-icon">
              <feather-icon icon="TagIcon" size="20" />
            </div>
            <div class="idm-metric-body">
              <span class="idm-metric-label">Prix par Commande</span>
              <span class="idm-metric-value">
                {{ selectedOrder.pricing_unit_price ? formatCurrency(selectedOrder.pricing_unit_price) : 'Négocié' }}
              </span>
            </div>
          </div>
          <div class="idm-metric idm-metric--green">
            <div class="idm-metric-icon">
              <feather-icon icon="PackageIcon" size="20" />
            </div>
            <div class="idm-metric-body">
              <span class="idm-metric-label">Commandes Livrées</span>
              <span class="idm-metric-value">{{ selectedOrder.number_of_orders }}</span>
            </div>
          </div>
          <div class="idm-metric idm-metric--purple">
            <div class="idm-metric-icon">
              <feather-icon icon="CreditCardIcon" size="20" />
            </div>
            <div class="idm-metric-body">
              <span class="idm-metric-label">Net à Payer</span>
              <span class="idm-metric-value idm-metric-value--highlight">{{ formatCurrency(selectedOrder.total_amount) }}</span>
            </div>
          </div>
        </div>

        <!-- ───── Entreprise warning ───── -->
        <div
          v-if="selectedOrder.pricing_mode === 'tiered' && !selectedOrder.pricing_tier_name"
          class="idm-warning"
        >
          <feather-icon icon="AlertTriangleIcon" size="15" class="mr-2 flex-shrink-0" />
          <span>Palier Entreprise — le montant total doit être saisi manuellement.</span>
        </div>

        <!-- ───── Flat-mode breakdown (legacy) ───── -->
        <div
          v-if="selectedOrder.pricing_mode !== 'tiered' && selectedOrder.pricing_breakdown"
          class="idm-flat-breakdown"
        >
          <div class="idm-flat-card idm-flat-card--orange">
            <div class="idm-flat-card-title">
              <feather-icon icon="TruckIcon" size="14" class="mr-1" />
              Sous-traitance
            </div>
            <p class="idm-flat-detail">{{ selectedOrder.pricing_breakdown.outsourcing_orders }} colis × {{ formatCurrency(selectedOrder.pricing_breakdown.outsourcing_price_per_order) }}</p>
            <p class="idm-flat-total">{{ formatCurrency(selectedOrder.pricing_breakdown.outsourcing_total) }}</p>
          </div>
          <div class="idm-flat-card idm-flat-card--teal">
            <div class="idm-flat-card-title">
              <feather-icon icon="HomeIcon" size="14" class="mr-1" />
              Livraison directe
            </div>
            <p class="idm-flat-detail">{{ selectedOrder.pricing_breakdown.company_orders }} colis × {{ formatCurrency(selectedOrder.pricing_breakdown.company_price_per_order) }}</p>
            <p class="idm-flat-total">{{ formatCurrency(selectedOrder.pricing_breakdown.company_total) }}</p>
          </div>
        </div>

        <!-- ───── Orders Table ───── -->
        <div class="idm-table-section">
          <div class="idm-table-header">
            <div class="idm-table-title">
              <feather-icon icon="ListIcon" size="16" class="mr-2" />
              Liste des commandes
            </div>
            <span class="idm-count-chip">{{ selectedOrder.number_of_orders }} colis</span>
          </div>

          <div v-if="loadingDetails" class="idm-loading">
            <b-spinner variant="primary" />
            <span class="ml-2 text-muted">Chargement des commandes…</span>
          </div>

          <b-table
            v-else
            :items="selectedOrder.order_details || []"
            :fields="orderDetailsFields"
            class="idm-table"
            responsive
            hover
            show-empty
            empty-text="Aucune commande trouvée"
          >
            <template #cell(delivered_at)="data">
              {{ formatDate(data.item.delivered_at) }}
            </template>
            <template #cell(price_ttc)="data">
              <span class="idm-price-cell">{{ formatCurrency(data.item.price_ttc) }}</span>
            </template>
          </b-table>
        </div>

        <!-- ───── Footer total bar ───── -->
        <div class="idm-footer">
          <span class="idm-footer-label">Net à Payer</span>
          <span class="idm-footer-amount">{{ formatCurrency(selectedOrder.total_amount) }}</span>
        </div>

      </div>
    </b-modal>

    <!-- Invoice Modal -->
    <b-modal
      v-model="showInvoiceModal"
      size="lg"
      hide-header
      hide-footer
      body-class="p-0"
      dialog-class="direx-invoice-modal"
    >
      <div class="direx-invoice-preview">
        <div class="direx-invoice-toolbar">
          <div>
            <strong>Règlement Direx #{{ formatInvoiceNumber(selectedOrder.id) }}</strong>
            <small class="d-block text-muted">Aperçu avant impression</small>
          </div>
          <div class="d-flex align-items-center">
            <b-button
              variant="outline-secondary"
              size="sm"
              class="mr-1"
              @click="showInvoiceModal = false"
            >
              Fermer
            </b-button>
            <b-button
              variant="danger"
              size="sm"
              :disabled="loadingInvoice"
              @click="printInvoice"
            >
              <feather-icon icon="PrinterIcon" size="14" class="mr-1" />
              Imprimer
            </b-button>
          </div>
        </div>

        <div v-if="loadingInvoice" class="direx-invoice-loading">
          <b-spinner variant="danger" />
          <span>Chargement de la facture…</span>
        </div>

        <article
          v-else
          id="direx-invoice-print"
          ref="direxInvoiceSheet"
          class="direx-invoice-sheet"
        >
          <div class="direx-top-accent">
            <span />
          </div>

          <header class="direx-document-header">
            <div class="direx-brand-block">
              <img
                src="/images/logo/logo-direx.png"
                alt="Direx"
                class="direx-logo"
              >
              <dl class="direx-client-details">
                <div><dt>Client :</dt><dd>{{ clientName }}</dd></div>
                <div><dt>Matricule fiscale :</dt><dd>{{ company.tax_identification_number || '-' }}</dd></div>
                <div><dt>Adresse :</dt><dd>{{ company.address || '-' }}</dd></div>
                <div><dt>Téléphone :</dt><dd>{{ clientPhone }}</dd></div>
              </dl>
            </div>
            <div class="direx-document-title">
              <h1>Règlement</h1>
              <dl>
                <div>
                  <dt>N.règlement :</dt>
                  <dd>{{ formatInvoiceNumber(selectedOrder.id) }}</dd>
                </div>
                <div>
                  <dt>Date :</dt>
                  <dd>{{ formatDate(selectedOrder.created_at) }}</dd>
                </div>
              </dl>
            </div>
          </header>

          <section class="direx-services-section">
            <table class="direx-services-table">
              <thead>
                <tr>
                  <th>Services</th>
                  <th>N°</th>
                  <th>Prix</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Colis livré</td>
                  <td>{{ selectedOrder.number_of_orders || 0 }}</td>
                  <td>{{ formatCurrency(selectedOrder.total_amount) }}</td>
                </tr>
              </tbody>
            </table>
          </section>

          <section class="direx-simple-total">
            <span>Total HT:</span>
            <strong>{{ formatCurrency(selectedOrder.total_amount) }}</strong>
          </section>

          <div class="direx-bottom-accent">
            <span />
          </div>
        </article>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BTable,
  BModal,
  BBadge,
  BButton,
  BPagination,
  BSpinner,
} from 'bootstrap-vue'
import axios from 'axios'
import store from '@/store/store'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'InvoiceManagement',
  components: {
    BTable,
    BModal,
    BBadge,
    BButton,
    BPagination,
    BSpinner,
  },
  data() {
    return {
      currentPage: 1,
      pageLength: 100,
      count: 0,
      invoices: [],
      currentFilter: 'all',
      showModal: false,
      showInvoiceModal: false,
      selectedOrder: {},
      loadingDetails: false,
      loadingInvoice: false,
      company: {},
      tableFields: [
        { key: 'id', label: 'N° Facture' },
        { key: 'start_date', label: 'Date Début' },
        { key: 'end_date', label: 'Date Fin' },
        { key: 'created_at', label: 'Crée le' },
        { key: 'number_of_orders', label: 'Nombre de colis' },
        { key: 'pricing_breakdown', label: 'Répartition' },
        { key: 'total_amount', label: 'Net A payer' },
        { key: 'status', label: 'Status' },
        { key: 'actions', label: 'Actions' },
      ],
      orderDetailsFields: [
        { key: 'id', label: 'BL' },
        { key: 'delivered_at', label: 'Date de livraison' },
        { key: 'price_ttc', label: 'Prix TTC' },
      ],
    }
  },
  computed: {
    filteredInvoices() {
      if (this.currentFilter === 'all') return this.invoices
      return this.invoices.filter(invoice => invoice.status === this.currentFilter)
    },
    isAdminDeveloper() {
      return store.state.user && store.state.user.role === 'admin_developer'
    },
    clientName() {
      return this.company.name || this.$branding.company.name || 'Carthage Delivery'
    },
    clientPhone() {
      const phones = [this.company.phone1, this.company.phone2].filter(Boolean)
      return phones.length ? phones.join(' / ') : '-'
    },
  },
  created() {
    this.getInvoices()
    this.getCompany()
  },
  methods: {
    async getCompany() {
      this.company = store.state.company || {}
      try {
        const response = await axios.get('/api/companies/')
        const companies = response.data.results || response.data
        this.company = Array.isArray(companies) && companies.length
          ? companies[0]
          : this.company
      } catch (error) {
        // Keep the branding fallback when company details are unavailable.
      }
    },
    async getInvoices() {
      try {
        const response = await axios.get('/api/invoices/invoice-providers/')
        this.invoices = response.data
        this.count = response.data.length
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching invoices:', error)
      }
    },
    async filterInvoices(filter) {
      this.currentFilter = filter
      try {
        const url = filter === 'all'
          ? '/api/invoices/invoice-providers/'
          : `/api/invoices/invoice-providers/${filter}_invoices/`
        const response = await axios.get(url)
        this.invoices = response.data
        this.count = response.data.length
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error filtering invoices:', error)
      }
    },
    formatDate(dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleDateString('fr-FR')
    },
    formatDateTime(dateTimeString) {
      if (!dateTimeString) return '-'
      return new Date(dateTimeString).toLocaleString('fr-FR')
    },
    formatCurrency(amount) {
      if (!amount) return '0 TND'
      return `${parseFloat(amount).toLocaleString('fr-FR', {
        minimumFractionDigits: 3,
        maximumFractionDigits: 3,
      })} TND`
    },
    getStatusVariant(status) {
      const variants = {
        unpaid: 'danger',
        paid: 'success',
        default: 'secondary',
      }
      return variants[status] || variants.default
    },
    async showOrderDetails(order) {
      this.selectedOrder = { ...order }
      this.showModal = true
      this.loadingDetails = true
      try {
        const response = await axios.get(`/api/invoices/invoice-providers/${order.id}/`)
        this.selectedOrder = response.data
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching invoice details:', error)
      } finally {
        this.loadingDetails = false
      }
    },
    async showInvoice(order) {
      this.selectedOrder = { ...order }
      this.showInvoiceModal = true
      this.loadingInvoice = true
      try {
        const response = await axios.get(`/api/invoices/invoice-providers/${order.id}/`)
        this.selectedOrder = response.data
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching printable invoice:', error)
      } finally {
        this.loadingInvoice = false
      }
    },
    formatInvoiceNumber(id) {
      return String(id || 0).padStart(4, '0')
    },
    printInvoice() {
      this.$nextTick(() => {
        const invoiceSheet = this.$refs.direxInvoiceSheet
        if (!invoiceSheet) return

        const printFrame = document.createElement('iframe')
        printFrame.setAttribute('title', 'Impression facture Direx')
        printFrame.style.position = 'fixed'
        printFrame.style.left = '-10000px'
        printFrame.style.top = '0'
        printFrame.style.width = '210mm'
        printFrame.style.height = '297mm'
        printFrame.style.border = '0'
        printFrame.style.opacity = '0'
        document.body.appendChild(printFrame)

        const printDocument = printFrame.contentDocument
        const printOverrides = `
          <style>
            @page { size: A4 portrait; margin: 0; }
            html, body {
              width: 210mm;
              min-height: 297mm;
              margin: 0 !important;
              padding: 0 !important;
              background: #fff !important;
            }
            *, *::before, *::after {
              box-sizing: border-box !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            #direx-invoice-print {
              position: relative !important;
              width: 210mm !important;
              max-width: none !important;
              min-height: 297mm !important;
              margin: 0 !important;
              padding: 18mm 15mm 20mm !important;
              overflow: hidden !important;
              color: #26262b !important;
              background: #fff !important;
              border: 0 !important;
              box-shadow: none !important;
              font-family: Montserrat, Arial, sans-serif !important;
              page-break-after: avoid !important;
              break-after: avoid-page !important;
            }
            #direx-invoice-print .direx-top-accent,
            #direx-invoice-print .direx-bottom-accent {
              position: absolute !important;
              left: 0 !important;
              right: 0 !important;
              height: 4mm !important;
              background: #d5d5d5 !important;
            }
            #direx-invoice-print .direx-top-accent { top: 0 !important; }
            #direx-invoice-print .direx-bottom-accent { bottom: 0 !important; }
            #direx-invoice-print .direx-top-accent span,
            #direx-invoice-print .direx-bottom-accent span {
              display: block !important;
              width: 70% !important;
              height: 100% !important;
              background: #ed1b2f !important;
              clip-path: polygon(0 0, 100% 0, calc(100% - 5mm) 100%, 0 100%) !important;
            }
            #direx-invoice-print .direx-document-header {
              display: flex !important;
              flex-direction: row !important;
              align-items: flex-start !important;
              justify-content: space-between !important;
              gap: 10mm !important;
            }
            #direx-invoice-print .direx-brand-block {
              flex: 1 1 auto !important;
            }
            #direx-invoice-print .direx-logo {
              display: block !important;
              width: 45mm !important;
              height: auto !important;
              object-fit: contain !important;
            }
            #direx-invoice-print .direx-document-title {
              width: auto !important;
              min-width: 77mm !important;
              text-align: right !important;
            }
            #direx-invoice-print .direx-document-title h1 {
              margin: 2mm 0 4mm !important;
              color: #050505 !important;
              font-size: 9mm !important;
              line-height: 1.15 !important;
              font-weight: 800 !important;
              letter-spacing: .03em !important;
            }
            #direx-invoice-print dl { margin: 0 !important; }
            #direx-invoice-print .direx-document-title dl div,
            #direx-invoice-print .direx-client-details div {
              display: grid !important;
              grid-template-columns: 33mm 1fr !important;
              gap: 3mm !important;
              margin-bottom: 1.1mm !important;
              font-size: 8pt !important;
              line-height: 1.25 !important;
            }
            #direx-invoice-print .direx-client-details {
              margin-top: 11mm !important;
            }
            #direx-invoice-print dt {
              color: #77777f !important;
              font-weight: 500 !important;
            }
            #direx-invoice-print dd {
              margin: 0 !important;
              color: #24242a !important;
              font-weight: 600 !important;
            }
            #direx-invoice-print .direx-services-section {
              margin-top: 14mm !important;
            }
            #direx-invoice-print .direx-services-table {
              width: 100% !important;
              border-collapse: separate !important;
              border-spacing: 0 !important;
            }
            #direx-invoice-print .direx-services-table th {
              padding: 3.2mm 3.7mm !important;
              background: #fde8ea !important;
              color: #16161b !important;
              font-size: 7.5pt !important;
              line-height: 1.2 !important;
              font-weight: 800 !important;
              text-transform: uppercase !important;
              letter-spacing: .04em !important;
              text-align: right !important;
            }
            #direx-invoice-print .direx-services-table th:first-child {
              width: 50% !important;
              border-radius: 7mm 0 0 7mm !important;
              text-align: left !important;
            }
            #direx-invoice-print .direx-services-table th:last-child {
              border-radius: 0 7mm 7mm 0 !important;
            }
            #direx-invoice-print .direx-services-table td {
              padding: 4mm 3.7mm !important;
              border-bottom: .25mm solid #ececef !important;
              color: #28282e !important;
              background: #fff !important;
              font-size: 8.5pt !important;
              line-height: 1.25 !important;
              text-align: right !important;
            }
            #direx-invoice-print .direx-services-table td:first-child {
              text-align: left !important;
            }
            #direx-invoice-print .direx-simple-total {
              display: flex !important;
              align-items: center !important;
              justify-content: space-between !important;
              width: 80mm !important;
              margin-top: 25mm !important;
              margin-left: auto !important;
              padding: 3mm 4mm !important;
              background: #fde8ea !important;
              border-radius: 7mm !important;
              color: #17171c !important;
              font-size: 8.5pt !important;
              line-height: 1.2 !important;
            }
            #direx-invoice-print .direx-simple-total strong {
              font-weight: 800 !important;
            }
          </style>`

        printDocument.open()
        printDocument.write(`<!doctype html><html lang="fr"><head><meta charset="utf-8"><title>Règlement Direx ${this.formatInvoiceNumber(this.selectedOrder.id)}</title>${printOverrides}</head><body>${invoiceSheet.outerHTML}</body></html>`)
        printDocument.close()

        const printWindow = printFrame.contentWindow
        const cleanup = () => {
          if (printFrame.parentNode) printFrame.parentNode.removeChild(printFrame)
        }
        printWindow.addEventListener('afterprint', cleanup, { once: true })

        const launchPrint = () => {
          printWindow.focus()
          printWindow.print()
          window.setTimeout(cleanup, 1000)
        }
        const fontsReady = printDocument.fonts && printDocument.fonts.ready
          ? printDocument.fonts.ready
          : Promise.resolve()
        const imagesReady = Array.from(printDocument.images).map(image => {
          if (image.complete) return Promise.resolve()
          return new Promise(resolve => {
            image.addEventListener('load', resolve, { once: true })
            image.addEventListener('error', resolve, { once: true })
          })
        })
        Promise.all([fontsReady, ...imagesReady]).then(launchPrint)
      })
    },
    onFiltered(filteredItems) {
      this.count = filteredItems.length
    },
    editInvoice(invoice) {
      // Navigate to edit page (we'll create this route)
      this.$router.push({ name: 'invoice-edit-direx', params: { id: invoice.id } })
    },
    async deleteInvoice(invoice) {
      // Prevent deletion of paid invoices
      if (invoice.status === 'paid') {
        this.showToast('warning', 'Impossible de supprimer une facture payée')
        return
      }

      try {
        const result = await this.$swal({
          title: 'Supprimer la facture?',
          text: `Êtes-vous sûr de vouloir supprimer la facture #${invoice.id}? Cette action est irréversible.`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Oui, supprimer',
          cancelButtonText: 'Annuler',
          customClass: {
            confirmButton: 'btn btn-danger',
            cancelButton: 'btn btn-outline-secondary ml-1',
          },
          buttonsStyling: false,
        })

        if (result.value) {
          await axios.delete(`/api/invoices/invoice-providers/${invoice.id}/`)

          this.showToast('success', 'Facture supprimée avec succès')

          // Refresh the list
          await this.getInvoices()
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error deleting invoice:', error)
        const errorMsg = error.response?.data?.error || 'Erreur lors de la suppression de la facture'
        this.showToast('danger', errorMsg)
      }
    },
    async toggleStatus(invoice) {
      // Prevent changing paid invoices back to unpaid
      if (invoice.status === 'paid') {
        this.showToast('warning', 'Une facture payée ne peut pas être marquée comme non payée')
        return
      }

      const newStatus = 'paid'

      try {
        const result = await this.$swal({
          title: 'Marquer comme payée?',
          text: `Êtes-vous sûr de vouloir marquer la facture #${invoice.id} comme payée? Cette action ne peut pas être annulée.`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Oui, marquer comme payée',
          cancelButtonText: 'Annuler',
          customClass: {
            confirmButton: 'btn btn-success',
            cancelButton: 'btn btn-outline-secondary ml-1',
          },
          buttonsStyling: false,
        })

        if (result.value) {
          await axios.post(`/api/invoices/invoice-providers/${invoice.id}/update_status/`, {
            status: newStatus,
          })

          this.showToast('success', `Facture #${invoice.id} marquée comme payée`)

          // Refresh the list
          await this.getInvoices()
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error updating status:', error)
        const errorMsg = error.response?.data?.error || 'Erreur lors du changement de statut'
        this.showToast('danger', errorMsg)
      }
    },
    showToast(variant, text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: variant === 'success' ? 'Succès' : 'Erreur',
          icon: variant === 'success' ? 'CheckCircleIcon' : 'AlertCircleIcon',
          text,
          variant,
        },
      })
    },
  },
}
</script>

<style scoped>
.invoice-management {
  padding: 1rem;
}

.cursor-pointer {
  cursor: pointer;
  transition: transform 0.2s;
}

.cursor-pointer:hover {
  transform: scale(1.05);
}

.button-group {
  display: flex;
  gap: 12px;
  margin-bottom: 1rem;
}

.filter-btn {
  padding: 10px 20px;
  border: 2px solid transparent;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s ease;
  background-color: #f8f9fa;
}

.all-btn {
  border-color: #3498db;
  color: #3498db;
}

.all-btn.active {
  background-color: #3498db;
  color: white;
}

.paid-btn {
  border-color: #2ecc71;
  color: #2ecc71;
}

.paid-btn.active {
  background-color: #2ecc71;
  color: white;
}

.unpaid-btn {
  border-color: #e74c3c;
  color: #e74c3c;
}

.unpaid-btn.active {
  background-color: #e74c3c;
  color: white;
}

.filter-btn:hover:not(.active) {
  background-color: rgba(0, 0, 0, 0.05);
}

.invoice-container {
  padding: 2rem;
}

.company-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #eee;
}

.company-info h3 {
  color: #2c3e50;
  margin-bottom: 1rem;
}

.invoice-info h2 {
  color: #2c3e50;
  margin-bottom: 1rem;
}

.client-info {
  margin-bottom: 2rem;
}

.client-info h4 {
  color: #e74c3c;
  margin-bottom: 1rem;
}

.label {
  color: #e74c3c;
  font-weight: 500;
  margin-right: 0.5rem;
}

.total-section {
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 2px solid #eee;
}

.total-row {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  font-size: 1.2rem;
}

.print-section {
  display: flex;
  justify-content: center;
  margin-top: 2rem;
}

.gap-2 {
  gap: 0.5rem;
}

.invoice-details {
  padding: 1.5rem;
}

.invoice-header {
  border-bottom: 1px solid #dee2e6;
  padding-bottom: 1rem;
}

.invoice-header h5 {
  color: #2c3e50;
  margin-bottom: 1rem;
}

/* ═══════════════════════════════════════════
   Invoice Detail Modal (IDM)
═══════════════════════════════════════════ */
.idm-wrapper {
  font-family: 'Inter', 'Roboto', sans-serif;
  border-radius: 12px;
  overflow: hidden;
}

/* Header */
.idm-header {
  background: linear-gradient(135deg, #1a1f36 0%, #2d3561 100%);
  padding: 1.5rem 1.75rem;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  position: relative;
}

.idm-header-left {
  flex: 1;
}

.idm-header-right {
  display: flex;
  align-items: center;
  margin-right: 2.5rem;
}

.idm-invoice-number {
  font-size: 1.5rem;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: -0.02em;
}

.idm-badges {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.idm-status-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.3rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.03em;
}

.idm-status-paid {
  background: rgba(39, 174, 96, 0.25);
  color: #6fcf97;
  border: 1px solid rgba(39, 174, 96, 0.4);
}

.idm-status-unpaid {
  background: rgba(235, 87, 87, 0.25);
  color: #f97171;
  border: 1px solid rgba(235, 87, 87, 0.4);
}

.idm-tier-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.3rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  background: rgba(86, 130, 244, 0.25);
  color: #93b4ff;
  border: 1px solid rgba(86, 130, 244, 0.4);
}

.idm-tier-enterprise {
  background: rgba(242, 153, 74, 0.25);
  color: #f2c98a;
  border: 1px solid rgba(242, 153, 74, 0.4);
}

.idm-period {
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.82rem;
  display: flex;
  align-items: center;
}

.idm-close-btn {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 8px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.7);
  transition: background 0.2s, color 0.2s;
}

.idm-close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
}

/* Metrics */
.idm-metrics {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0;
  border-bottom: 1px solid #e9ecef;
}

@media (max-width: 768px) {
  .idm-metrics {
    grid-template-columns: 1fr;
  }
}

.idm-metric {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  border-right: 1px solid #e9ecef;
  position: relative;
  overflow: hidden;
  transition: background 0.2s;
}

.idm-metric:last-child {
  border-right: none;
}

.idm-metric::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
}

.idm-metric--blue::before  { background: linear-gradient(90deg, #4facfe, #00f2fe); }
.idm-metric--green::before { background: linear-gradient(90deg, #43e97b, #38f9d7); }
.idm-metric--purple::before { background: linear-gradient(90deg, #a18cd1, #fbc2eb); }

.idm-metric-icon {
  flex-shrink: 0;
  width: 46px;
  height: 46px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0;
}

.idm-metric--blue   .idm-metric-icon { background: #e8f4fd; color: #2196f3; }
.idm-metric--green  .idm-metric-icon { background: #e8faf0; color: #27ae60; }
.idm-metric--purple .idm-metric-icon { background: #f3e8ff; color: #7c3aed; }

.idm-metric-body {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.idm-metric-label {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: #9ca3af;
  font-weight: 600;
}

.idm-metric-value {
  font-size: 1.3rem;
  font-weight: 700;
  color: #1a1f36;
  line-height: 1.1;
}

.idm-metric-value--highlight {
  color: #7c3aed;
}

/* Warning */
.idm-warning {
  display: flex;
  align-items: center;
  margin: 0 1.5rem 0;
  padding: 0.75rem 1rem;
  background: #fffbeb;
  border: 1px solid #fcd34d;
  border-radius: 8px;
  color: #92400e;
  font-size: 0.85rem;
  margin-top: 1.25rem;
}

/* Flat-mode breakdown */
.idm-flat-breakdown {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
}

@media (max-width: 576px) {
  .idm-flat-breakdown { grid-template-columns: 1fr; }
}

.idm-flat-card {
  border-radius: 10px;
  padding: 1rem 1.25rem;
  border: 1px solid #e9ecef;
}

.idm-flat-card--orange {
  background: linear-gradient(135deg, #fff9f0, #fff3e0);
  border-left: 4px solid #f59e0b;
}

.idm-flat-card--teal {
  background: linear-gradient(135deg, #f0fdf4, #e6fffa);
  border-left: 4px solid #10b981;
}

.idm-flat-card-title {
  font-weight: 700;
  font-size: 0.85rem;
  color: #374151;
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}

.idm-flat-detail {
  font-size: 0.82rem;
  color: #6b7280;
  margin: 0.2rem 0;
}

.idm-flat-total {
  font-size: 1.05rem;
  font-weight: 700;
  color: #1a1f36;
  margin: 0.4rem 0 0;
}

/* Table section */
.idm-table-section {
  padding: 1.25rem 1.5rem;
}

.idm-table-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
}

.idm-table-title {
  display: flex;
  align-items: center;
  font-weight: 700;
  font-size: 0.9rem;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.idm-count-chip {
  background: #1a1f36;
  color: #fff;
  border-radius: 20px;
  padding: 0.25rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.03em;
}

.idm-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

.idm-table {
  font-size: 0.875rem;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e9ecef;
}

.idm-table >>> thead th {
  background: #f8fafc !important;
  color: #4b5563 !important;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  border-bottom: 2px solid #e9ecef !important;
  padding: 0.75rem 1rem !important;
}

.idm-table >>> tbody td {
  padding: 0.65rem 1rem !important;
  color: #374151;
  vertical-align: middle;
}

.idm-table >>> tbody tr:hover td {
  background: #f0f7ff !important;
}

.idm-price-cell {
  font-weight: 600;
  color: #1a1f36;
}

/* Footer total bar */
.idm-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.75rem;
  background: linear-gradient(135deg, #1a1f36 0%, #2d3561 100%);
}

.idm-footer-label {
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: rgba(255, 255, 255, 0.6);
}

.idm-footer-amount {
  font-size: 1.4rem;
  font-weight: 800;
  color: #ffffff;
  letter-spacing: -0.02em;
}

/* ─── legacy ─── */
.order-list {
  margin-top: 1.5rem;
}

.logo {
  width: 170px;  /* Adjust this value as needed for normal display */
  height: auto;
  padding: 0.5rem;
}

/* Pricing breakdown styles */
.pricing-summary {
  font-size: 0.85rem;
  line-height: 1.3;
}

.pricing-breakdown-detail {
  background-color: #f8f9fa;
  padding: 1rem;
  border-radius: 6px;
  border: 1px solid #dee2e6;
}

.pricing-card-small {
  background-color: white;
  padding: 0.75rem;
  border-radius: 4px;
  border: 1px solid #dee2e6;
  margin-bottom: 0.5rem;
}

.pricing-card-small.outsourcing {
  border-left: 4px solid #f39c12;
}

.pricing-card-small.company {
  border-left: 4px solid #27ae60;
}

.pricing-card-small p {
  margin: 0.25rem 0;
  font-size: 0.9rem;
}

.pricing-card-small .total {
  color: #2c3e50;
  font-weight: bold;
}

/* ═══════════════════════════════════════════
   Printable Direx invoice
═══════════════════════════════════════════ */
.direx-invoice-preview {
  background: #eef0f3;
  padding: 1rem;
}

.direx-invoice-toolbar {
  max-width: 760px;
  margin: 0 auto 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.direx-invoice-loading {
  min-height: 500px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  background: #fff;
}

.direx-invoice-sheet {
  position: relative;
  max-width: 760px;
  min-height: 920px;
  margin: 0 auto;
  padding: 58px 50px 72px;
  overflow: hidden;
  color: #26262b;
  background: #fff;
  border: 1px solid #d8d8dc;
  box-shadow: 0 12px 34px rgba(25, 29, 38, 0.12);
  font-family: 'Montserrat', 'Inter', Arial, sans-serif;
}

.direx-top-accent,
.direx-bottom-accent {
  position: absolute;
  left: 0;
  right: 0;
  height: 15px;
  background: #d5d5d5;
}

.direx-top-accent { top: 0; }
.direx-bottom-accent { bottom: 0; }

.direx-top-accent span,
.direx-bottom-accent span {
  display: block;
  width: 70%;
  height: 100%;
  background: #ed1b2f;
  clip-path: polygon(0 0, 100% 0, calc(100% - 18px) 100%, 0 100%);
}

.direx-document-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 2rem;
}

.direx-logo {
  display: block;
  width: 170px;
  height: auto;
  object-fit: contain;
}

.direx-document-title {
  min-width: 290px;
  text-align: right;
}

.direx-document-title h1 {
  margin: 8px 0 16px;
  color: #050505;
  font-size: 2.25rem;
  font-weight: 800;
  letter-spacing: 0.03em;
}

.direx-document-title dl,
.direx-client-details {
  margin: 0;
}

.direx-document-title dl div,
.direx-client-details div {
  display: grid;
  grid-template-columns: 125px 1fr;
  gap: 0.75rem;
  margin-bottom: 0.42rem;
  font-size: 0.78rem;
}

.direx-document-title dt,
.direx-client-details dt {
  color: #77777f;
  font-weight: 500;
}

.direx-document-title dd,
.direx-client-details dd {
  margin: 0;
  color: #24242a;
  font-weight: 600;
}

.direx-client-details {
  margin-top: 42px;
}

.direx-client-details div {
  grid-template-columns: 125px 1fr;
  font-size: 0.72rem;
}

.direx-services-section { margin-top: 48px; }

.direx-services-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}

.direx-services-table th {
  padding: 12px 14px;
  background: #fde8ea;
  color: #16161b;
  font-size: 0.7rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  text-align: right;
}

.direx-services-table th:first-child {
  width: 50%;
  border-radius: 24px 0 0 24px;
  text-align: left;
}

.direx-services-table th:last-child { border-radius: 0 24px 24px 0; }

.direx-services-table td {
  padding: 15px 14px;
  border-bottom: 1px solid #ececef;
  color: #28282e;
  font-size: 0.78rem;
  text-align: right;
}

.direx-services-table td:first-child { text-align: left; }

.direx-simple-total {
  width: 300px;
  margin: 92px 0 0 auto;
  padding: 10px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fde8ea;
  border-radius: 22px;
  color: #17171c;
  font-size: 0.8rem;
}

.direx-simple-total strong { font-weight: 800; }

@media (max-width: 767px) {
  .direx-invoice-sheet { padding: 42px 24px 62px; }
  .direx-document-header { flex-direction: column; }
  .direx-document-title { min-width: 0; width: 100%; text-align: left; }
  .direx-simple-total { width: 100%; }
}

</style>
